// components/PropertyIssues/NewIssueModal.jsx
import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Dropzone from "react-dropzone";
import { addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";
import PropertyContext from "../../contexts/PropertyContext";
import { createMeta } from "../../utils/auditFields";
import { propertyCollection } from "../../utils/firestorePaths";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(720px, 92vw)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
};

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", color: "success" },
  { value: "medium", label: "Medium", color: "warning" },
  { value: "high", label: "High", color: "error" },
  { value: "urgent", label: "Urgent", color: "error" },
];

const ASSIGNED_TO_OPTIONS = ["IT", "Operations", "Facilities", "Marketing"];

export default function NewIssueModal({ open, onClose, vendors }) {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [priority, setPriority] = useState("medium");
  const [assignedTo, setAssignedTo] = useState("");
  const [desc, setDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [reportTo, setReportTo] = useState([]);

  const propertyIssuesRef = propertyCollection(
    selectedClient,
    selectedProperty,
    "propertyIssues"
  );

  const handleFileDrop = async (acceptedFiles) => {
    const date = new Date().toISOString().split("T")[0];
    const uploads = await Promise.all(
      acceptedFiles.map(async (file) => {
        const storageRef = ref(
          storage,
          `property-issues/${selectedProperty}/${date}/${Date.now()}-${
            file.name
          }`
        );
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        return { name: file.name, url, size: file.size, type: file.type };
      })
    );
    setFiles((prev) => [...prev, ...uploads]);
  };

  const handleAddChip = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      setReportTo((prev) => [...prev, e.target.value.trim()]);
      e.target.value = "";
    }
  };

  const handleDeleteChip = (chipToDelete) => () =>
    setReportTo((chips) => chips.filter((c) => c !== chipToDelete));

  const clear = () => {
    setSelectedVendor("");
    setSelectedStatus("");
    setPriority("medium");
    setAssignedTo("");
    setDesc("");
    setFiles([]);
    setReportTo([]);
  };

  const handleSave = async () => {
    if (!selectedVendor) return onClose(); // simple guard like your original

    await addDoc(propertyIssuesRef, {
      completed: false,
      vendor: selectedVendor || "unknown",
      status: selectedStatus || "not reported",
      issueDetails: desc || "",
      files,
      reportTo,
      priority,
      assignedTo: assignedTo || "",
      submittedAt: serverTimestamp(),
      ...createMeta(),
    });
    clear();
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h6">New Issue</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr 1fr" gap={2}>
          <Box>
            <InputLabel id="vendor-label">Select Vendor</InputLabel>
            <Select
              labelId="vendor-label"
              fullWidth
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
            >
              {vendors.map((v) => (
                <MenuItem key={v.id} value={v.name}>
                  {v.name}
                </MenuItem>
              ))}
              <MenuItem value="unknown">unknown</MenuItem>
              <MenuItem value="TBD">TBD</MenuItem>
            </Select>
          </Box>

          <Box>
            <InputLabel id="status-label">Select Status</InputLabel>
            <Select
              labelId="status-label"
              fullWidth
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {[
                "not reported",
                "reported",
                "pending estimate",
                "pending approval",
                "pending vendor arrival",
                "work in progress",
                "completed",
              ].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <InputLabel id="priority-label">Priority</InputLabel>
            <Select
              labelId="priority-label"
              fullWidth
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <InputLabel id="assigned-to-label">Assigned To</InputLabel>
            <Select
              labelId="assigned-to-label"
              fullWidth
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {ASSIGNED_TO_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <Box mt={2}>
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
          />
        </Box>

        <Box mt={2}>
          <Dropzone onDrop={handleFileDrop}>
            {({ getRootProps, getInputProps }) => (
              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed gray",
                  p: 2,
                  textAlign: "center",
                  borderRadius: 1,
                }}
              >
                <input {...getInputProps()} />
                <Typography>
                  Drag & drop files here, or click to select files
                </Typography>
              </Box>
            )}
          </Dropzone>
          <Box mt={1} display="flex" gap={1} flexWrap="wrap">
            {files.map((f, i) => (
              <Chip key={i} label={f.name} />
            ))}
          </Box>
        </Box>

        <Box mt={2}>
          <FormControl fullWidth>
            <InputLabel shrink htmlFor="report-to-input">
              Reported To
            </InputLabel>
            <TextField
              id="report-to-input"
              placeholder="Type and press Enter to add"
              onKeyDown={handleAddChip}
            />
          </FormControl>
          <Box mt={1} display="flex" gap={1} flexWrap="wrap">
            {reportTo.map((chip, i) => (
              <Chip key={i} label={chip} onDelete={handleDeleteChip(chip)} />
            ))}
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button
            onClick={() => {
              clear();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Create Issue
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
